class FwYoutubePlayer extends HTMLElement {
    videoId;
    videoPoster;
    videoTitle;
    openInFancybox;
    playerInstance;

    #hasKlaro;
    #klaroManager;

    constructor() {
        super();
    }

    connectedCallback() {
        this.videoId = this.getAttribute('video-id');
        this.videoPoster = this.getAttribute('video-poster');
        this.videoTitle = this.getAttribute('video-title');
        this.openInFancybox = this.getAttribute('open-in-fancybox') && this.getAttribute('open-in-fancybox') !== 'false';

        this.render();
        this.addEventListeners();
    }

    render() {
        this.innerHTML = `
            <button aria-label="${(this.videoTitle ? `${this.videoTitle} abspielen` : 'Video abspielen')}">
                ${
                    this.videoPoster
                    ? this.createPlaceholder(this.videoId, this.videoPoster)
                    : this.createPlaceholder(this.videoId, false)
                }
            </button>
        `;
    }
    async postRender() {
        // get current placeholder image and yt thumbnail url
        const placeholderImg = this.querySelector('.fw-youtube-player-placeholder img') as HTMLImageElement;

        // replace placeholder image with yt thumbnail if placeholder image couldnt load
        if (!placeholderImg.complete) {
            placeholderImg.addEventListener('load', () => {
                if (!placeholderImg.complete) {
                    this.replaceThumbnail(placeholderImg);
                }
            });
            placeholderImg.addEventListener('error', () => {
                this.replaceThumbnail(placeholderImg);
            });
        }
    }

    createPlaceholder(id, posterUrl) {
        const placeholderUrl = posterUrl || `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/hqdefault.jpg&originalDimension=1`;

        return `
            <div class="fw-youtube-player-placeholder">
                <img src="${placeholderUrl}" role="presentation">
                <div class="fw-youtube-player-placeholder-play"></div>
            </div>
        `;
    }
    addEventListeners() {
        const button = this.querySelector('button');
        button.addEventListener('click', () => {
            this.onButtonClick();
        });

        document.addEventListener('DOMContentLoaded', () => {
            this.postRender();
        });
    }
    onButtonClick() {
        // check if klaro is available
        this.#hasKlaro = window.klaro !== undefined;

        // get klaro manager if found
        if (this.#hasKlaro) {
            this.#klaroManager = window.klaro.getManager();
        }

        // check if user has consented to youtube
        const hasConsented = this.#hasKlaro ? this.#klaroManager.consents.youtube : true;

        // create popup or iframe based on consent
        if (this.openInFancybox) {
            hasConsented ? this.createFancybox() : this.createPopup({ fancybox: true });
        } else {
            hasConsented ? this.createIframe() : this.createPopup({ fancybox: false });
        }
    }
    createPopup(options) {
        const defaultStrings = {
            de: {
                title: "Zustimmung erforderlich",
                text: "Damit Sie dieses Video ansehen können, müssen Sie der<br />Verwendung von <b>Youtube</b> zustimmen.",
                accept: "Zustimmen",
                decline: "Ablehnen",
            },
            en: {
                title: "Permission required",
                text: "This website uses <b>Youtube</b> to provide video contents.<br/> If you want to continue, you have to allow the usage otherwise we are unable to provide the contents of this video.",
                accept: "Accept",
                decline: "Decline",
            },
        };
        const htmllang = document.documentElement.lang.toLowerCase();
        const isSupportedLanguage = Object.keys(defaultStrings).indexOf(htmllang) !== -1;
        const lang = isSupportedLanguage ? htmllang : "en";

        const klaroElem = document.querySelector("#klaro .klaro");

        if (!klaroElem) {
            document.createElement("div");
            klaroElem.className = "klaro";
        }

        klaroElem.innerHTML = `
            <div class="cookie-modal">
                <div class="cm-bg"></div>
                <div class="cm-modal cm-klaro">
                    <div class="cm-header">
                        <h1 class="title"><span>${defaultStrings[lang].title}</span></h1>
                        <p>${defaultStrings[lang].text}</p>
                    </div>
                    <div class="cm-footer">
                        <div class="cm-footer-buttons">
                            <button class="cm-btn cm-btn-decline" type="button">${defaultStrings[lang].decline}</button>
                            <button class="cm-btn cm-btn-success" type="button">${defaultStrings[lang].accept}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        const klaroElemAccept = klaroElem.querySelector(".cm-btn.cm-btn-success");
        const klaroElemDecline = klaroElem.querySelector(".cm-btn.cm-btn-decline");

        klaroElemAccept.addEventListener("click", () => {
            options.fancybox ? this.createFancybox() : this.createIframe();
            if (this.#hasKlaro) {
                this.#klaroManager.updateConsent('youtube', true);
                this.#klaroManager.saveAndApplyConsents();
            }
            this.destroyPopup();
        });
        klaroElemDecline.addEventListener("click", () => {
            if (this.#hasKlaro) {
                this.#klaroManager.updateConsent('youtube', false);
                this.#klaroManager.saveAndApplyConsents();
            }
            this.destroyPopup();
        });
    }
    destroyPopup() {
        const klaroElem = document.querySelector("#klaro .klaro");
        klaroElem.innerHTML = "";
    }
    createIframe() {
        this.loadScriptByURL('youtube-iframe-sdk', 'https://www.youtube.com/iframe_api', () => {
            window.YT.ready(() => {
                const iframe = document.createElement('div');
                const button = this.querySelector('button');

                this.replaceChild(iframe, button);

                const player = new window.YT.Player(iframe, {
                    height: '360',
                    width: '640',
                    videoId: this.videoId,
                    playerVars: {
                        autoplay: 1,
                        playsinline: 0,
                    },
                    events: {
                        onReady: (event) => {
                            this.playerInstance = player;
                            this.classList.remove("loading");
                            this.classList.add("player-ready");

                            // trigger event on ready
                            var readyEvent = new Event("player.ready");
                            this.dispatchEvent(readyEvent);

                            // play video
                            event.target.playVideo();
                        }
                    },
                });


            });
        });
    }
    createFancybox() {
        window.Fancybox.show([
            {
                src: `https://www.youtube.com/watch?v=${this.videoId}?autoplay=1`,
                type: "video",
                id: "fw-youtube-popup",
                ratio: 16 / 9,
                videoAutoplay: true,
            }
        ])
    }

    loadScriptByURL(id, url, callback) {
        const isScriptExist = document.getElementById(id);

        if (!isScriptExist) {
            var script = document.createElement("script");
            script.type = "text/javascript";
            script.src = url;
            script.id = id;
            script.onload = function () {
                if (callback) callback();
            };
            document.body.appendChild(script);
        }

        if (isScriptExist && callback) callback();
    }

    async replaceThumbnail(elem) {
        const ytThumbSrc = await this.getYtThumbnailUrl(this.videoId);
        elem.src = ytThumbSrc;
    }

    getYtThumbnailUrl(id) {
        // Helper function to load an image and return a promise
        const loadImage = (src) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = src;
                img.onload = () => resolve(src);
                img.onerror = () => reject();
            });
        };

        // array of possible thumbnail urls in order of quality
        const urls = [
            `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/maxresdefault.jpg&originalDimension=1`,
            `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/hqdefault.jpg&originalDimension=1`,
            `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/mqdefault.jpg&originalDimension=1`,
            `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/sddefault.jpg&originalDimension=1`,
            `/includes/futureweb/imageWrapperExt.php?file=https://img.youtube.com/vi/${id}/default.jpg&originalDimension=1`,
        ];

        // Attempt to load images in order, returning the first successful load
        return urls.reduce((chain, url) => {
            return chain.catch(() => loadImage(url));
        }, Promise.reject());
    }
}

customElements.define('fw-youtube-player', FwYoutubePlayer);